// @ts-nocheck
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { RefreshCw, Download, Trash2, Check, Wifi, WifiOff, Sparkles, Lock, Rocket, Loader2 } from "lucide-react";
import { getOllamaStatus, getOllamaModels, pullModel, redeployModel, updateSettings, getSettings, type UserSettings } from "../services/api";

const DEFAULT_SETTINGS: Partial<UserSettings> = {
  ollama_endpoint: "http://localhost:11434",
  ollama_model: "sexy_ai_ultimate:latest",
  temperature: 0.9,
  top_p: 0.92,
  context_size: 2048,
  max_tokens: 512,
  repeat_penalty: 1.2,
  stop_sequences: "<|im_end|>,<|im_start|>",
  stream: true,
};

// 模型别名映射 - Ollama 模型名 -> 显示名称
const MODEL_ALIASES: Record<string, string> = {
  "sexy_ai_ultimate:latest": "Ultimate NSFW",
  "sexy_ai_ultimate": "Ultimate NSFW",
  "qwen2.5:7b": "Qwen 2.5 (7B)",
  "my_girlfriend:latest": "My Girlfriend (Qwen2.5)",
  "my_girlfriend": "My Girlfriend (Qwen2.5)",
};

// 精选模型列表 - 在设置面板中优先展示
const FEATURED_MODELS = [
  { id: "qwen2.5:7b", name: "Qwen 2.5", desc: "通用模型 · 全场景对话", emoji: "🤖" },
  { id: "sexy_ai_ultimate:latest", name: "Ultimate NSFW", desc: "成人角色专用 · Qwen2.5", emoji: "🔞" },
  { id: "my_girlfriend:latest", name: "My Girlfriend", desc: "女友角色 · Qwen2.5", emoji: "💋" },
];

interface Props {
  currentUser?: { username: string; role: string } | null;
  onBack?: () => void;
}

export function OllamaSettingsPanel({ currentUser, onBack }: Props) {
  const [status, setStatus] = useState<{ online: boolean; model: string; available_models: string[] } | null>(null);
  const [settings, setSettings] = useState<Partial<UserSettings>>({ ...DEFAULT_SETTINGS });
  const [initialized, setInitialized] = useState(false);
  const [pullModelName, setPullModelName] = useState("");
  const [pulling, setPulling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [redeploying, setRedeploying] = useState(false);
  const [redeployResult, setRedeployResult] = useState<{ success: boolean; message: string; steps: any[] } | null>(null);
  const [showRedeployDetail, setShowRedeployDetail] = useState(false);
  const [redeployModelName, setRedeployModelName] = useState("sexy_ai_ultimate");

  const isAdmin = currentUser?.role === "admin";

  useEffect(() => {
    loadStatus();
    loadSettings();
  }, []);

  async function loadStatus() {
    try {
      const s = await getOllamaStatus();
      setStatus(s);
    } catch {}
  }

  async function loadSettings() {
    try {
      const s = await getSettings();
      // 后端返回的值合并默认值（确保所有参数都有值）
      setSettings({ ...DEFAULT_SETTINGS, ...s });
      setInitialized(true);
    } catch {
      // 加载失败就用前端默认值
      setInitialized(true);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      // 普通用户只能保存对话参数
      const payload = isAdmin
        ? settings
        : {
            temperature: settings.temperature,
            top_p: settings.top_p,
            context_size: settings.context_size,
            max_tokens: settings.max_tokens,
            repeat_penalty: settings.repeat_penalty,
            stop_sequences: settings.stop_sequences,
          };
      await updateSettings(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
    setSaving(false);
  }

  async function handlePull() {
    if (!pullModelName.trim()) return;
    setPulling(true);
    try {
      await pullModel(pullModelName.trim());
      setPullModelName("");
      loadStatus();
    } catch (e) {
      alert(e instanceof Error ? e.message : "拉取失败");
    }
    setPulling(false);
  }

  async function handleRedeploy() {
    setRedeploying(true);
    setRedeployResult(null);
    setShowRedeployDetail(false);
    try {
      const result = await redeployModel(redeployModelName);
      setRedeployResult(result);
      if (result.success) {
        loadStatus();
        loadSettings();
      }
    } catch (e) {
      setRedeployResult({
        success: false,
        message: e instanceof Error ? e.message : "部署失败",
        steps: [],
      });
    }
    setRedeploying(false);
  }

  return (
    <div className="h-full overflow-y-auto bg-slate-950">
      <div className="max-w-3xl mx-auto p-6">
        <h1 className="text-xl font-bold text-white flex items-center gap-2 mb-2">
          {onBack && (
            <button
              onClick={onBack}
              className="p-1 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
            >
              ←
            </button>
          )}
          <Sparkles className="w-5 h-5 text-rose-400" />
          模型设置
        </h1>
        <p className="text-xs text-slate-400 mb-6">配置本地 AI 模型，Lok AI 通过 Ollama 驱动所有对话</p>

        {/* 状态卡片 */}
        <div className="mb-6 p-4 rounded-2xl bg-slate-900/50 border border-slate-800">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {status?.online ? (
                <div className="flex items-center gap-1.5 text-green-400">
                  <Wifi className="w-4 h-4" />
                  <span className="text-sm font-medium">Ollama 已连接</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-red-400">
                  <WifiOff className="w-4 h-4" />
                  <span className="text-sm font-medium">Ollama 未连接</span>
                </div>
              )}
            </div>
            <button onClick={loadStatus} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {status?.online && (
            <div className="text-xs text-slate-400 space-y-1">
              <p>当前模型：<span className="text-white font-medium">{MODEL_ALIASES[status.model] || status.model}</span></p>
              <p>已安装模型：{status.available_models.length} 个</p>
            </div>
          )}

          {!status?.online && (
            <div className="mt-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-400">
              请先安装并启动 Ollama：<code className="bg-slate-800 px-1 rounded">ollama serve</code>
            </div>
          )}
        </div>

        {/* 模型管理 - 仅管理员可见 */}
        {isAdmin && (
          <div className="mb-6 bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
            <h2 className="text-sm font-semibold text-slate-200 mb-4">模型选择</h2>

            {/* 精选模型卡片 */}
            {status?.online && (
              <div className="grid grid-cols-3 gap-3 mb-4">
                {FEATURED_MODELS.map((fm) => {
                  const isAvailable = status.available_models?.includes(fm.id);
                  const isActive = settings.ollama_model === fm.id;
                  return (
                    <button
                      key={fm.id}
                      onClick={() => isAvailable && setSettings((prev) => ({ ...prev, ollama_model: fm.id }))}
                      disabled={!isAvailable}
                      className={`relative p-4 rounded-xl text-left transition-all ${
                        isActive
                          ? "bg-gradient-to-br from-rose-500/20 to-purple-500/20 border-2 border-rose-500/50"
                          : isAvailable
                            ? "bg-slate-800/80 border-2 border-transparent hover:border-slate-600"
                            : "bg-slate-800/30 border-2 border-transparent opacity-40 cursor-not-allowed"
                      }`}
                    >
                      {isActive && (
                        <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-rose-400 animate-pulse" />
                      )}
                      <div className="text-2xl mb-2">{fm.emoji}</div>
                      <div className="text-sm font-semibold text-white">{fm.name}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{fm.desc}</div>
                      {!isAvailable && (
                        <div className="text-[10px] text-amber-400 mt-1">未安装</div>
                      )}
                      {isActive && (
                        <div className="text-[10px] text-rose-400 mt-1 font-medium">当前使用</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* 全部模型列表 */}
            {status?.available_models && status.available_models.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-slate-400 mb-2">全部已安装模型</p>
                <div className="space-y-1">
                  {status.available_models.map((m) => {
                    const displayName = MODEL_ALIASES[m] || m;
                    const isActive = settings.ollama_model === m;
                    return (
                      <div
                        key={m}
                        onClick={() => setSettings((prev) => ({ ...prev, ollama_model: m }))}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                          isActive ? "bg-rose-500/10 border border-rose-500/30" : "bg-slate-800/50 hover:bg-slate-800"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {isActive && <Check className="w-3.5 h-3.5 text-rose-400" />}
                          <span className="text-sm text-white">{displayName}</span>
                          {displayName !== m && (
                            <span className="text-[10px] text-slate-500">{m}</span>
                          )}
                        </div>
                        {isActive && (
                          <span className="text-[10px] text-rose-400 bg-rose-500/10 px-2 py-0.5 rounded-full">当前使用</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <p className="text-xs text-slate-400 mb-2">拉取新模型</p>
              <div className="flex gap-2">
                <input
                  value={pullModelName}
                  onChange={(e) => setPullModelName(e.target.value)}
                  placeholder="例如：qwen2.5:14b"
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-rose-500/50"
                />
                <button
                  onClick={handlePull}
                  disabled={pulling || !pullModelName.trim()}
                  className="px-4 py-2 rounded-xl bg-purple-600 text-white text-sm hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1.5"
                >
                  {pulling ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {pulling ? "拉取中..." : "拉取"}
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {["qwen2.5:7b", "qwen2.5:14b", "glm4:9b", "llama3.1:8b"].map((m) => (
                  <button
                    key={m}
                    onClick={() => setPullModelName(m)}
                    className="px-2 py-0.5 rounded text-[10px] bg-slate-800 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 一键更新模型 - 仅管理员可见 */}
        {isAdmin && (
          <div className="mb-6 bg-gradient-to-br from-amber-500/5 to-orange-500/5 border border-amber-500/20 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Rocket className="w-4 h-4 text-amber-400" />
                <h2 className="text-sm font-semibold text-slate-200">一键更新模型</h2>
              </div>
              <span className="text-[10px] text-slate-500 bg-slate-800/50 px-2 py-0.5 rounded-full">
                Modelfile → Ollama → LOK
              </span>
            </div>
            <p className="text-xs text-slate-400 mb-4">
              从 Modelfile 源文件重新构建模型，自动清理旧版本和缓存，热更新到当前平台
            </p>

            {/* 选择要部署的模型 */}
            <div className="mb-4">
              <label className="text-xs text-slate-400 mb-1.5 block">目标模型</label>
              <select
                value={redeployModelName}
                onChange={(e) => setRedeployModelName(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500/50"
              >
                <option value="sexy_ai_ultimate">{MODEL_ALIASES["sexy_ai_ultimate:latest"]} (sexy_ai_ultimate)</option>
                <option value="my_girlfriend">{MODEL_ALIASES["my_girlfriend:latest"]} (my_girlfriend)</option>
              </select>
            </div>

            {/* 部署结果 */}
            {redeployResult && (
              <div className={`mb-4 p-4 rounded-xl border ${
                redeployResult.success
                  ? "bg-green-500/10 border-green-500/30"
                  : "bg-red-500/10 border-red-500/30"
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {redeployResult.success ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <div className="w-4 h-4 rounded-full bg-red-400" />
                  )}
                  <span className={`text-sm font-medium ${redeployResult.success ? "text-green-400" : "text-red-400"}`}>
                    {redeployResult.message}
                  </span>
                  {redeployResult.model && (
                    <span className="text-[10px] text-slate-500">{redeployResult.model}</span>
                  )}
                  {redeployResult.success && (redeployResult as any).preset_applied && (
                    <div className="mt-2 pt-2 border-t border-green-500/20">
                      <p className="text-[10px] text-green-400/80 mb-1">参数预设已自动应用：</p>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries((redeployResult as any).preset_applied).map(([k, v]) => (
                          <span key={k} className="text-[9px] bg-green-500/15 text-green-400 px-2 py-0.5 rounded-full">
                            {k}: {v as any}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* 步骤详情 */}
                <button
                  onClick={() => setShowRedeployDetail(!showRedeployDetail)}
                  className="text-[10px] text-slate-400 hover:text-white transition-colors mb-2"
                >
                  {showRedeployDetail ? "收起详情 ▲" : "查看详情 ▼"}
                </button>
                {showRedeployDetail && redeployResult.steps.length > 0 && (
                  <div className="space-y-1">
                    {redeployResult.steps.map((s: any) => (
                      <div key={s.step} className="flex items-start gap-2 text-[11px]">
                        <span className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
                          s.status === "success" ? "bg-green-500/20 text-green-400" :
                          s.status === "warning" ? "bg-amber-500/20 text-amber-400" :
                          "bg-red-500/20 text-red-400"
                        }`}>
                          {s.step}
                        </span>
                        <div className="flex-1 min-w-0">
                          <span className="text-slate-300">{s.name}</span>
                          {s.output && <span className="text-slate-500 ml-1.5 truncate">— {s.output}</span>}
                          {s.error && <span className="text-red-400 ml-1.5 truncate">— {s.error}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={handleRedeploy}
              disabled={redeploying}
              className={`w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all ${
                redeploying
                  ? "bg-amber-600/50 cursor-wait"
                  : "bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
              }`}
            >
              {redeploying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  正在部署（约30-60秒）...
                </>
              ) : (
                <>
                  <Rocket className="w-4 h-4" />
                  一键更新模型
                </>
              )}
            </motion.button>
          </div>
        )}

        {/* 普通用户提示 */}
        {!isAdmin && (
          <div className="mb-6 p-4 rounded-2xl bg-slate-900/50 border border-slate-800">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="w-4 h-4 text-slate-500" />
              <h2 className="text-sm font-semibold text-slate-200">模型选择</h2>
            </div>
            <p className="text-xs text-slate-400">
              当前使用模型：<span className="text-white font-medium">{MODEL_ALIASES[settings.ollama_model || ""] || settings.ollama_model || "未知"}</span>
            </p>
            <p className="text-[10px] text-slate-500 mt-1">模型切换仅管理员可用，如需更改请联系管理员</p>
          </div>
        )}

        {/* 对话参数 - 所有用户可用 */}
        <div className="mb-6 bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
          <h2 className="text-sm font-semibold text-slate-200 mb-4">对话参数</h2>

          <div className="grid grid-cols-2 gap-4">
            {[
              { key: "temperature", label: "温度 (Temperature)", min: 0, max: 2, step: 0.1, desc: "越高越有创意" },
              { key: "top_p", label: "Top P", min: 0, max: 1, step: 0.1, desc: "核采样参数" },
              { key: "context_size", label: "上下文长度", min: 512, max: 32768, step: 512, desc: "影响记忆长度" },
              { key: "max_tokens", label: "最大回复长度", min: 50, max: 8000, step: 50, desc: "AI 回复字数上限" },
              { key: "repeat_penalty", label: "重复惩罚 (Repeat Penalty)", min: 1, max: 2, step: 0.05, desc: "越高越不重复" },
            ].map((param) => (
              <div key={param.key}>
                <label className="flex items-center justify-between text-xs text-slate-400 mb-1.5">
                  <span>{param.label}</span>
                  <span className="text-white font-medium">{settings[param.key as keyof UserSettings]}</span>
                </label>
                <input
                  type="range"
                  min={param.min}
                  max={param.max}
                  step={param.step}
                  value={settings[param.key as keyof UserSettings] ?? 0}
                  onChange={(e) => setSettings((prev) => ({ ...prev, [param.key]: parseFloat(e.target.value) }))}
                  className="w-full accent-rose-500"
                />
                <p className="text-[10px] text-slate-500 mt-0.5">{param.desc}</p>
              </div>
            ))}
          </div>

          {/* 停止词 */}
          <div className="mt-4">
            <label className="flex items-center justify-between text-xs text-slate-400 mb-1.5">
              <span>停止词 (Stop Sequences)</span>
              <span className="text-slate-500">可选</span>
            </label>
            <input
              type="text"
              value={settings.stop_sequences || ""}
              onChange={(e) => setSettings((prev) => ({ ...prev, stop_sequences: e.target.value }))}
              placeholder="多个停止词用英文逗号分隔，如: User:, Response:, <|eot_id|>"
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-rose-500/50"
            />
            <p className="text-[10px] text-slate-500 mt-1">AI 遇到这些词时立即停止生成，留空则不设置停止词</p>
          </div>
        </div>

        {/* API 端点 - 仅管理员可见 */}
        {isAdmin && (
          <div className="mb-6 bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
            <h2 className="text-sm font-semibold text-slate-200 mb-4">API 端点</h2>
            <input
              value={settings.ollama_endpoint || "http://localhost:11434"}
              onChange={(e) => setSettings((prev) => ({ ...prev, ollama_endpoint: e.target.value }))}
              className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-rose-500/50"
            />
          </div>
        )}

        {/* 保存按钮 */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleSave}
          disabled={saving}
          className={`w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all ${
            saved ? "bg-green-600" : "bg-gradient-to-r from-rose-500 to-purple-600 hover:from-rose-600 hover:to-purple-700"
          }`}
        >
          {saved ? <Check className="w-4 h-4" /> : null}
          {saved ? "已保存" : saving ? "保存中..." : "保存设置"}
        </motion.button>
      </div>
    </div>
  );
}
